import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { TheoryEveningRequest } from "../../features/organization/contracts/TheoryEveningRequest";
import { MAX_EVENINGS, WEEKDAYS, nextFreeWeekday, weekdayLabel } from "./fahrschulePresetDefaults";

interface TheoryEveningRowsProps {
  evenings: TheoryEveningRequest[];
  disabled: boolean;
  onChange: (evenings: TheoryEveningRequest[]) => void;
}

/** Up to five weekday + start-time rows; a weekday another row uses is not offered twice. */
const TheoryEveningRows: React.FC<TheoryEveningRowsProps> = ({ evenings, disabled, onChange }) => {
  const { t, i18n } = useTranslation();

  const update = (index: number, change: Partial<TheoryEveningRequest>) =>
    onChange(evenings.map((evening, i) => (i === index ? { ...evening, ...change } : evening)));
  const remove = (index: number) => onChange(evenings.filter((_, i) => i !== index));
  const add = () => onChange([...evenings, { dayOfWeek: nextFreeWeekday(evenings), timeLocal: "18:30" }]);

  return (
    <fieldset>
      <legend className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
        {t("preset.fahrschule.evenings")}
      </legend>
      <div className="space-y-2">
        {evenings.map((evening, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <select
              aria-label={t("preset.fahrschule.weekday")}
              className="input w-40"
              value={evening.dayOfWeek}
              disabled={disabled}
              onChange={(e) => update(index, { dayOfWeek: e.target.value })}
            >
              {WEEKDAYS.filter((day) => day === evening.dayOfWeek || !evenings.some((o) => o.dayOfWeek === day)).map(
                (day) => (
                  <option key={day} value={day}>
                    {weekdayLabel(day, i18n.language)}
                  </option>
                )
              )}
            </select>
            <input
              type="time"
              aria-label={t("preset.fahrschule.time")}
              className="input w-32 dark:[color-scheme:dark]"
              value={evening.timeLocal}
              disabled={disabled}
              onChange={(e) => update(index, { timeLocal: e.target.value })}
            />
            <button
              type="button"
              aria-label={t("preset.fahrschule.removeEvening")}
              className="rounded-md p-2 text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
              disabled={disabled}
              onClick={() => remove(index)}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
      {evenings.length < MAX_EVENINGS && (
        <button type="button" className="btn btn-secondary mt-2" disabled={disabled} onClick={add}>
          <Plus size={16} />
          <span>{t("preset.fahrschule.addEvening")}</span>
        </button>
      )}
    </fieldset>
  );
};

export default TheoryEveningRows;
